/**
 * Replays progress events over a progress snapshot, the way the server's
 * training_apply_events applies them. Used on load for progress kept from an earlier
 * visit that still hasn't reached the server, so it shows instead of looking undone
 * (#1200 review, Codex P2).
 */
import type { TrainingEvent } from './trainingApi';

export interface ReplayProgress {
  lessonsDone: Record<string, boolean>;
  qStats: Record<string, { seen: number; right: number; wrong: number }>;
  missed: Record<string, boolean>;
  cardKnown: Record<string, boolean>;
  bitesRead: Record<string, boolean>;
  lastLesson: { id: string; section: number } | null;
}

export function replayEvents<T extends ReplayProgress>(state: T, events: TrainingEvent[]): T {
  if (!events.length) return state;
  const next = {
    ...state,
    lessonsDone: { ...state.lessonsDone },
    qStats: { ...state.qStats },
    missed: { ...state.missed },
    cardKnown: { ...state.cardKnown },
    bitesRead: { ...state.bitesRead },
  };
  for (const ev of events) {
    switch (ev.type) {
      case 'lesson_done': next.lessonsDone[ev.id] = true; break;
      case 'bite_read': next.bitesRead[ev.id] = true; break;
      case 'card_known': next.cardKnown[ev.id] = true; break;
      case 'card_unknown': delete next.cardKnown[ev.id]; break;
      case 'last_lesson': next.lastLesson = { id: ev.id, section: ev.value }; break;
      case 'question_seen':
      case 'question_right':
      case 'question_wrong': {
        const prev = next.qStats[ev.id] ?? { seen: 0, right: 0, wrong: 0 };
        next.qStats[ev.id] = {
          seen: prev.seen + 1,
          right: prev.right + (ev.type === 'question_right' ? 1 : 0),
          wrong: prev.wrong + (ev.type === 'question_wrong' ? 1 : 0),
        };
        if (ev.type === 'question_right') delete next.missed[ev.id];
        if (ev.type === 'question_wrong') next.missed[ev.id] = true;
        break;
      }
      default: break;
    }
  }
  return next;
}
